import { cn } from './utils';

/**
 * Color-coded badge for the asset submission.status enum (pending →
 * under_review → approved/rejected).
 *
 * SIL-322: consolidated from 4 copies (customer submissions/index.tsx,
 * customer submissions/$submissionId.tsx, ops submissions_.$submissionId.tsx,
 * ops submissions.tsx). Three of the four shared the same rounded-pill visual
 * (the `default` variant here); ops's dense list table (submissions.tsx) used
 * a visually distinct bordered/uppercase/small style for its dense table —
 * preserved as the `dense` variant rather than silently unifying 3 different
 * looks into 1.
 */

const LABELS: Record<string, string> = {
  pending: 'pending',
  under_review: 'under review',
  approved: 'approved',
  rejected: 'rejected',
};

const DEFAULT_STYLES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-800',
  under_review: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
};

const DENSE_STYLES: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-800 border-amber-300',
  under_review: 'bg-blue-50 text-blue-700 border-blue-300',
  approved: 'bg-green-50 text-green-700 border-green-300',
  rejected: 'bg-red-50 text-red-700 border-red-300',
};

/** Pure label function — used directly by the badge and unit-tested. */
export function submissionStatusLabel(status: string): string {
  return LABELS[status] ?? status.replace(/_/g, ' ');
}

export function SubmissionStatusBadge({
  value,
  variant = 'default',
}: {
  value: string;
  /** 'dense' = bordered/uppercase small text, for ops's dense list table. */
  variant?: 'default' | 'dense';
}) {
  const label = submissionStatusLabel(value);

  if (variant === 'dense') {
    const style = DENSE_STYLES[value] ?? 'bg-muted text-muted-foreground border-border';
    return (
      <span
        className={cn(
          'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
          style,
        )}
      >
        {label}
      </span>
    );
  }

  const style = DEFAULT_STYLES[value] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        style,
      )}
    >
      {label}
    </span>
  );
}
